import { type Accessor, type Component, batch, createSignal, onCleanup, onMount } from "solid-js";
import { TextSelectionMenu } from "./TextSelectionMenu";

// Handles text selection for markdown content
export const TextSelectionHandler: Component<{
  ref: Accessor<HTMLElement | undefined>;
  isReadOnly: boolean;
}> = (props) => {
  const [selection, setSelection] = createSignal<Selection>();
  const [domRect, setDomRect] = createSignal<DOMRect>();
  let isClosing = false;

  const resetPanel = () => {
    isClosing = true;
    batch(() => {
      setSelection();
      setDomRect();
    });
    // Reset the closing flag after a short delay
    setTimeout(() => {
      isClosing = false;
    }, 100);
  };

  // The text selection menu should only pop up after a mouse up event or touch end event
  // to prevent the modal from showing up while the user is still selecting
  const onSelectionEnd = () => {
    // If we're in the process of closing, don't reopen
    if (isClosing) return;

    // Small delay to ensure selection state is settled
    setTimeout(() => {
      if (isClosing) return;

      const selection = getSelection();
      if (!selection) return;

      batch(() => {
        if (!selection) return resetPanel();
        if (!props.ref()?.contains(selection.anchorNode) || !props.ref()?.contains(selection.focusNode))
          return resetPanel();
        if (selection.rangeCount === 0 || selection.isCollapsed) return resetPanel();

        setSelection(selection);
        setDomRect(selection.getRangeAt(0).getBoundingClientRect());
      });
    }, 10);
  };

  onMount(() => {
    document.addEventListener("mouseup", onSelectionEnd);
    document.addEventListener("touchend", onSelectionEnd);
    onCleanup(() => {
      document.removeEventListener("mouseup", onSelectionEnd);
      document.removeEventListener("touchend", onSelectionEnd);
    });
  });

  return (
    <TextSelectionMenu
      isReadOnly={props.isReadOnly}
      rect={domRect()}
      getText={() => selection()?.toString() || ""}
      onClose={resetPanel}
    />
  );
};
